import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthProvider'
import { HappyTaskLoadingLogo } from '../components/icons/HappyTaskLoadingLogo'
import { navigateToHome } from './navigateToHome'
import { useEffect } from 'react'

function MainTransition() {
  const navigate = useNavigate()

  useEffect(() => {
    navigateToHome(navigate)
  }, [navigate])

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      gap: '24px',
      animation: 'fadeIn 0.3s ease-in'
    }}>
      <HappyTaskLoadingLogo />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  )
}

function OnboardingTransition() {
  const navigate = useNavigate()

  useEffect(() => {
    const triggerOnboardingTransition = async () => {
      if (window.api) {
        try {
          await window.api.invoke('transition-to-onboarding')
        } catch (error) {
          console.error('[OnboardingTransition] IPC call failed:', error)
          navigate('/onboarding', { replace: true })
        }
      } else {
        navigate('/onboarding', { replace: true })
      }
    }
    triggerOnboardingTransition()
  }, [navigate])

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    }}>
      <HappyTaskLoadingLogo />
    </div>
  )
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth()
  const location = useLocation()
  const loginMethod = localStorage.getItem('loginMethod')

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        gap: '24px',
        animation: 'fadeIn 0.3s ease-in'
      }}>
        <HappyTaskLoadingLogo />
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
      </div>
    )
  }

  const isOfflineMode = loginMethod === 'offline'
  const offlineSetupCompleted = localStorage.getItem('offlineSetupCompleted') === 'true'

  const hasAccess = isAuthenticated || (isOfflineMode && offlineSetupCompleted) || (isOfflineMode && location.pathname !== '/home')

  if (!hasAccess) {
    return <Navigate to="/" replace />
  }

  if (isOfflineMode && !offlineSetupCompleted && location.pathname === '/home') {
    return <Navigate to="/setup-profile" replace />
  }

  const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true'

  if (location.pathname === '/setup-profile' && isAuthenticated) {
    const profileSetupCompleted = localStorage.getItem('profileSetupCompleted') === 'true'

    if (profileSetupCompleted) {
      return onboardingCompleted ? <MainTransition /> : <OnboardingTransition />
    }

    const hasFullName = user?.user_metadata?.full_name
    if (hasFullName) {
      localStorage.setItem('profileSetupCompleted', 'true')
      return onboardingCompleted ? <MainTransition /> : <OnboardingTransition />
    }

    if (loginMethod === 'password') {
      localStorage.setItem('profileSetupCompleted', 'true')
      return onboardingCompleted ? <MainTransition /> : <OnboardingTransition />
    }
  }

  if (location.pathname === '/home' && !onboardingCompleted) {
    if (isOfflineMode && offlineSetupCompleted) {
      // Offline user with completed setup can access home
    } else {
      return <OnboardingTransition />
    }
  }

  return <>{children}</>
}
